/**
 * ObjectPicker — inline type-ahead combobox over any manifest object kind
 * (entity, surface, cloud function, event type, firestore path).
 *
 * Deliberately parallel to EntityPicker.tsx rather than sharing code with
 * it — EntityPicker is shared, tested infrastructure for Impact and Trace;
 * this component exists so the sandbox editor's generalization work never
 * has to touch that shared surface. See the plan's "Scope decisions".
 *
 * Keyboard model identical to EntityPicker:
 *   ↑/↓ move highlight · Enter selects · Escape closes + restores ·
 *   Tab closes + commits text as-is.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { filterPickable, type PickableObject } from '../state/pickableObjects.js';

export interface ObjectPickerProps {
  items: readonly PickableObject[];
  value?: string;
  ariaLabel: string;
  placeholder?: string;
  onChange: (id: string) => void;
}

export function ObjectPicker({
  items,
  value = '',
  ariaLabel,
  placeholder = 'Pick an object…',
  onChange,
}: ObjectPickerProps): React.JSX.Element {
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setQuery(value);
  }, [value, open]);

  const options = useMemo(() => filterPickable(items, query), [items, query]);

  useEffect(() => {
    if (highlight >= options.length) setHighlight(0);
  }, [options.length, highlight]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const commit = (id: string): void => {
    onChange(id);
    setQuery(id);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(options.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = options[highlight] ?? options[0];
      if (pick) commit(pick.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery(value);
    }
  };

  return (
    <div ref={rootRef} className="object-picker">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open && options[highlight] ? `${listboxId}-${highlight}` : undefined}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && options.length > 0 && (
        <ul id={listboxId} role="listbox" className="object-picker-listbox">
          {options.map((item, i) => (
            <li
              key={item.id}
              id={`${listboxId}-${i}`}
              role="option"
              aria-selected={i === highlight}
              className={`object-picker-option${i === highlight ? ' is-active' : ''}`}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(ev) => {
                ev.preventDefault();
                commit(item.id);
              }}
            >
              <span className="mono">{item.id}</span>
              <span className="object-picker-subtitle">· {item.subtitle}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
